import { useState } from "react";
import { Link } from "react-router-dom";

const stories = [
  {
    id: 1,
    type: "Investor Case Study",
    icon: "ri-user-star-line",
    name: "George & Patricia Whitmore",
    location: "Harrogate, Yorkshire",
    invested: "£75,000",
    year: "2017",
    monthlyIncome: "£625 / month",
    totalReceived: "£37,500+",
    quote: "We were looking for a reliable, asset-backed income stream after retiring. Thorpe Dellow's Social Housing product has paid us every single month without fail since 2017. The documentation was thorough and the team was transparent from day one.",
    avatar: "https://readdy.ai/api/search-image?query=candid%20photo%20of%20a%20retired%20British%20couple%20in%20their%2060s%2C%20sitting%20in%20a%20living%20room%2C%20warm%20natural%20light%2C%20genuine%20happy%20expressions%2C%20authentic%20real%20people%20photography%2C%20shallow%20depth%20of%20field&width=80&height=80&seq=601&orientation=squarish",
    badge: "8 Years · Rolled Over Twice",
    badgeColor: "#C9A84C",
    outcome: "Capital returned in full at end of first term. Elected to roll over for a second and third term.",
  },
  {
    id: 2,
    type: "Property Case Study",
    icon: "ri-building-2-line",
    name: "Birchfield House, Leeds",
    location: "West Yorkshire",
    invested: "£1.2M",
    year: "2016",
    monthlyIncome: "£10,000 / month",
    totalReceived: "£960,000+",
    quote: "Acquired in 2016 and immediately leased to a registered housing association under a 20-year FRI agreement. The property houses 14 adults in supported living, generating consistent monthly lease income with CPI-linked increases every year since acquisition.",
    avatar: "https://readdy.ai/api/search-image?query=real%20photo%20of%20a%20clean%20modern%20UK%20supported%20housing%20residential%20property%20exterior%2C%20red%20brick%20two%20storey%20building%2C%20tidy%20garden%2C%20overcast%20British%20daylight%2C%20Leeds%2C%20authentic%20documentary%20photography&width=80&height=80&seq=602&orientation=squarish",
    badge: "20-Year FRI Lease · Fully Occupied",
    badgeColor: "#4A9B7F",
    outcome: "Zero void periods in 8+ years. Lease income exceeds original projections by 6% following CPI increases.",
  },
  {
    id: 3,
    type: "Investor Case Study",
    icon: "ri-medal-line",
    name: "Richard Caldwell",
    location: "Bristol, England",
    invested: "£120,000",
    year: "2020",
    monthlyIncome: "£1,000 / month",
    totalReceived: "£60,000+",
    quote: "I invested through my IFA who introduced me to Thorpe Dellow during Covid-19, when every other asset class was unpredictable. The social housing product just kept paying throughout the entire pandemic. It is exactly what it says on the tin.",
    avatar: "https://readdy.ai/api/search-image?query=candid%20photo%20of%20a%20British%20man%20aged%2055%20in%20a%20smart%20casual%20jacket%2C%20relaxed%20genuine%20expression%2C%20sitting%20in%20a%20comfortable%20home%20study%2C%20warm%20natural%20window%20light%2C%20documentary%20style%20photography%2C%20real%20person%20feel&width=80&height=80&seq=603&orientation=squarish",
    badge: "Invested Through Covid — Zero Interruptions",
    badgeColor: "#C9A84C",
    outcome: "Full 5-year term completed. Capital returned in full. Re-invested at renewal for new 5-year term.",
  },
];

const companyFacts = [
  { icon: "ri-calendar-event-line", value: "Est. 2005", label: "Founded in Leeds" },
  { icon: "ri-home-4-line", value: "35", label: "Properties Owned" },
  { icon: "ri-group-line", value: "545+", label: "Residential Units" },
  { icon: "ri-money-pound-circle-line", value: "£47M+", label: "Assets Under Management" },
  { icon: "ri-percent-line", value: "100%", label: "Lease Income Compliance" },
  { icon: "ri-award-line", value: "20 Yrs", label: "Track Record" },
];

export default function CompanySuccessSection() {
  const [activeStory, setActiveStory] = useState(0);
  const story = stories[activeStory];

  return (
    <>
      {/* ── WHO WE ARE STRIP ── */}
      <section className="bg-white py-16 md:py-20 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Left — Company intro */}
            <div>
              <span className="inline-flex items-center gap-2 text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.2em] bg-[#C9A84C]/08 border border-[#C9A84C]/20 px-3 py-1.5 rounded-full mb-5">
                <i className="ri-building-4-line text-xs leading-none" />
                Est. June 2005 · Leeds, Yorkshire
              </span>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#071A2E] leading-tight mb-5"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Twenty Years of
                <br />
                <span className="text-[#C9A84C]">Doing This One Thing Well</span>
              </h2>
              <p className="text-slate-500 text-sm leading-relaxed mb-4">
                Thorpe Property Development Ltd — trading as <strong className="text-[#071A2E]">Thorpe Dellow</strong> — was founded in Leeds in June 2005 on a single conviction: that UK social housing represented the most undersupplied, government-supported, and recession-resistant asset class in the country.
              </p>
              <p className="text-slate-500 text-sm leading-relaxed mb-6">
                Two decades later, with over 35 assisted living properties, £47M+ in assets under management, and 545+ residential units across England, Wales and Scotland, that conviction has proven right — through the 2008 financial crisis, the 2020 pandemic, and every economic cycle in between. Our investors have never missed a monthly payment.
              </p>

              {/* Portfolio image */}
              <div className="w-full rounded-2xl overflow-hidden mb-6" style={{ height: 220 }}>
                <img
                  src="https://readdy.ai/api/search-image?query=aerial%20drone%20photography%20of%20Yorkshire%20England%20residential%20property%20investment%20portfolio%2C%20rows%20of%20clean%20red%20brick%20houses%20with%20neat%20gardens%2C%20quiet%20tree-lined%20suburban%20streets%2C%20warm%20golden%20hour%20afternoon%20light%2C%20professional%20real%20estate%20investment%20photography%2C%20no%20people%2C%20wide%20cinematic%20shot%2C%20tidy%20well%20maintained%20British%20neighbourhood&width=700&height=220&seq=807&orientation=landscape"
                  alt="Thorpe Dellow property portfolio — Yorkshire"
                  className="w-full h-full object-cover object-center"
                />
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  "Section 21 FSMA 2000 Compliant",
                  "Independent Security Trustee",
                  "First Legal Charge Security",
                  "No Void Periods Since 2006",
                ].map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 bg-[#EEF2F9] border border-slate-200 px-3 py-1.5 rounded-full font-medium">
                    <i className="ri-check-line text-[#C9A84C] text-xs leading-none" />
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#071A2E] border-b border-[#C9A84C] pb-0.5 hover:text-[#C9A84C] transition-colors cursor-pointer"
              >
                Read the full company story
                <i className="ri-arrow-right-line leading-none" />
              </Link>
            </div>

            {/* Right — Stat grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {companyFacts.map((f) => (
                <div
                  key={f.label}
                  className="bg-[#EEF2F9] rounded-2xl p-5 flex flex-col items-center text-center hover:border-[#C9A84C]/25 border border-transparent transition-all"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C9A84C]/12 mb-3">
                    <i className={`${f.icon} text-[#C9A84C] text-lg leading-none`} />
                  </div>
                  <p className="text-xl font-bold text-[#071A2E] leading-none mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{f.value}</p>
                  <p className="text-[11px] text-slate-500 leading-tight">{f.label}</p>
                </div>
              ))}

              {/* Extra visual card */}
              <div className="col-span-2 md:col-span-3 rounded-2xl overflow-hidden" style={{ height: 160 }}>
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20UK%20property%20investment%20team%20in%20a%20modern%20Leeds%20office%20reviewing%20property%20portfolio%20documents%20and%20investment%20returns%2C%20warm%20natural%20light%2C%20business%20meeting%2C%20corporate%20professional%20atmosphere%2C%20no%20faces%20visible%2C%20wide%20angle%20office%20photography&width=700&height=160&seq=808&orientation=landscape"
                  alt="Thorpe Dellow investment team"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SUCCESS STORIES ── */}
      <section
        className="py-20 md:py-28 relative overflow-hidden"
        style={{ background: "linear-gradient(160deg, #071A2E 0%, #0D2B4A 60%, #071A2E 100%)" }}
      >
        {/* Decorative */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none opacity-10" style={{ background: "radial-gradient(circle, #C9A84C 0%, transparent 70%)", transform: "translate(30%, -30%)" }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none opacity-8" style={{ background: "radial-gradient(circle, #C9A84C 0%, transparent 70%)", transform: "translate(-30%, 30%)" }} />
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: "linear-gradient(90deg, transparent, #C9A84C 30%, #E2C27A 50%, #C9A84C 70%, transparent)" }} />

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 bg-[#C9A84C]/12 border border-[#C9A84C]/25 text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
              <i className="ri-trophy-line text-xs leading-none" />
              Real Results · Real Investors
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Success Stories
            </h2>
            <p className="text-slate-400 text-sm max-w-xl mx-auto leading-relaxed">
              From individual high net worth investors to property acquisitions spanning nearly a decade — here is what Thorpe Dellow looks like in practice.
            </p>
          </div>

          {/* Tab switcher — flex-wrap for mobile */}
          <div className="flex items-center justify-center gap-2 mb-10 flex-wrap px-4">
            {stories.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setActiveStory(i)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeStory === i
                    ? "bg-[#C9A84C] text-[#071A2E]"
                    : "text-slate-400 hover:text-white bg-white/5 hover:bg-white/10"
                }`}
              >
                {s.type}
              </button>
            ))}
          </div>

          {/* Story Card */}
          <div className="max-w-4xl mx-auto">
            <div
              className="rounded-3xl overflow-hidden border border-white/10"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-5">

                {/* Left stat panel */}
                <div
                  className="lg:col-span-2 p-6 md:p-8 flex flex-col justify-between gap-6"
                  style={{ background: "rgba(201,168,76,0.07)", borderRight: "1px solid rgba(201,168,76,0.15)" }}
                >
                  <div>
                    <span
                      className="inline-block text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-full mb-5"
                      style={{ background: `${story.badgeColor}20`, color: story.badgeColor, border: `1px solid ${story.badgeColor}40` }}
                    >
                      {story.badge}
                    </span>
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-16 h-16 flex-shrink-0 rounded-2xl overflow-hidden border-2 border-[#C9A84C]/30">
                        <img src={story.avatar} alt={story.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>{story.name}</p>
                        <p className="text-slate-400 text-xs mt-0.5 flex items-center gap-1">
                          <i className="ri-map-pin-2-line text-[#C9A84C] text-xs leading-none" />
                          {story.location}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: "Amount Invested", value: story.invested },
                      { label: "Year", value: story.year },
                      { label: "Monthly Income", value: story.monthlyIncome },
                      { label: "Total Received", value: story.totalReceived },
                    ].map((s) => (
                      <div key={s.label} className="bg-white/5 rounded-xl px-3 py-3">
                        <p className="text-[10px] text-slate-500 mb-0.5">{s.label}</p>
                        <p className="text-sm font-bold text-[#C9A84C]">{s.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white/5 rounded-xl px-4 py-3 border border-[#C9A84C]/15">
                    <p className="text-[10px] text-[#C9A84C] font-bold uppercase tracking-wide mb-1">Outcome</p>
                    <p className="text-slate-300 text-xs leading-relaxed">{story.outcome}</p>
                  </div>
                </div>

                {/* Right quote panel */}
                <div className="lg:col-span-3 p-6 md:p-10 flex flex-col justify-between gap-8">
                  <div>
                    <i className="ri-double-quotes-l text-[#C9A84C]/30 text-5xl leading-none block mb-4" />
                    <blockquote className="text-white/85 text-base md:text-lg leading-relaxed italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                      "{story.quote}"
                    </blockquote>
                    <p className="text-[#C9A84C] text-xs font-semibold mt-5">— {story.name}, {story.location}</p>
                  </div>

                  <div className="border-t border-white/8 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-shield-check-fill text-[#C9A84C] text-sm leading-none" />
                      </div>
                      <span className="text-slate-500 text-xs">Verified Thorpe Dellow Investor/Asset</span>
                    </div>
                    <Link
                      to="/social-housing"
                      className="inline-flex items-center gap-1.5 text-[#C9A84C] text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap"
                    >
                      View the product details
                      <i className="ri-arrow-right-line leading-none" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Story indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {stories.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveStory(i)}
                className={`rounded-full transition-all cursor-pointer ${activeStory === i ? "w-8 h-2 bg-[#C9A84C]" : "w-2 h-2 bg-white/20 hover:bg-white/40"}`}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
